import { Stack, Typography } from '@mui/material';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import { Dropdown } from 'common/form';
import { getStatusOptions } from 'modules/analyse/utils/getStatusOptions';
import { useRecoilValue } from 'recoil';
import { statusSetAtom } from 'services/candidate/atoms';
import {
  RequirementConditionOperator,
  RequirementConditionType
} from 'generated-graphql/types';
import {
  rulesetInputContainer,
  rulesetInputWidth
} from 'modules/analyse/components/RuleSettingItem';
import { Input } from 'components/Inputs';
import { formatScoreScaled } from 'modules/analyse/utils/formatScoreScaled';
import { ConversationStatus } from 'generated-graphql/graphql';
import { RuleConversationAutomationType } from 'modules/analyse-automations/types/RuleConversationAutomationType';
import { conversationAutomationTaskTypeOptions } from 'modules/analyse-automations/constants/conversationAutomationTaskTypeOptions';
import { conversationAutomationStatusOptions } from 'modules/analyse-automations/constants/conversationAutomationStatusOptions';
import { getConversationAutomationTaskType } from 'modules/analyse-automations/utils/getConversationAutomationTaskType';
import { getConversationAutomationScoreOperator } from 'modules/analyse-automations/utils/getConversationAutomationScoreOperator';
import { getConversationAutomationScore } from 'modules/analyse-automations/utils/getConversationAutomationScore';
import { getConversationAutomationStatus } from 'modules/analyse-automations/utils/getConversationAutomationStatus';
import { getScoreScaleFormatter } from 'modules/analyse-automations/utils/getScoreScaleFormatter';
import { scoreConditionOptions } from 'modules/analyse/constants/scoreConditionOptions';

type RuleSettingConversationAutomationItemProps = {
  analysisAutomationItem: AutomationFormItem;
  onChangeAnalysisAutomationItem: (value: AutomationFormItem) => void;
};

export const RuleSettingConversationAutomationItem: React.FC<
  RuleSettingConversationAutomationItemProps
> = ({ analysisAutomationItem, onChangeAnalysisAutomationItem }) => {
  const statusSet = useRecoilValue(statusSetAtom);

  const handleChangeCandidateStatus = (value: string) => {
    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      action: {
        ...analysisAutomationItem.action,
        candidateStatus: value
      }
    });
  };

  const handleChangeConversationAutomationTaskType = (
    value: RuleConversationAutomationType
  ) => {
    switch (value) {
      case RuleConversationAutomationType.CONVERSATION_SCORE:
        onChangeAnalysisAutomationItem({
          ...analysisAutomationItem,
          requirementConditions: [
            {
              type: RequirementConditionType.ConversationScore,
              operator: RequirementConditionOperator.EqualToOrGreaterThan,
              preferredAnswerScore: 0.7
            },
            // NOTE: We always add this, even though it's hidden
            {
              type: RequirementConditionType.ConversationMandatoryQuestionsMet,
              operator: RequirementConditionOperator.IsTrue
            }
          ]
        });
        break;
      case RuleConversationAutomationType.CONVERSATION_STATUS:
        onChangeAnalysisAutomationItem({
          ...analysisAutomationItem,
          requirementConditions: [
            {
              type: RequirementConditionType.ConversationStatusCondition,
              operator: RequirementConditionOperator.Equals,
              conversationStatus: ConversationStatus.Completed
            }
          ]
        });
        break;
      case RuleConversationAutomationType.CONVERSATION_MANDATORY_QUESTIONS_MET:
        onChangeAnalysisAutomationItem({
          ...analysisAutomationItem,
          requirementConditions: [
            {
              type: RequirementConditionType.ConversationMandatoryQuestionsMet,
              operator: RequirementConditionOperator.IsTrue
            }
          ]
        });
        break;
      case RuleConversationAutomationType.CONVERSATION_MANDATORY_QUESTIONS_NOT_MET:
        onChangeAnalysisAutomationItem({
          ...analysisAutomationItem,
          requirementConditions: [
            {
              type: RequirementConditionType.ConversationMandatoryQuestionsMet,
              operator: RequirementConditionOperator.IsFalse
            }
          ]
        });
        break;
      default:
        console.warn(`Unhandled conversation automation type: ${value}`);
        break;
    }
  };

  const handleChangeConversationScoreOperator = (
    value: RequirementConditionOperator
  ) => {
    const updatedRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (condition.type === RequirementConditionType.ConversationScore) {
          return {
            ...condition,
            operator: value
          };
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: updatedRequirementConditions
    });
  };

  const handleChangeConversationScore = (value: string) => {
    const updatedRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (condition.type === RequirementConditionType.ConversationScore) {
          return {
            ...condition,
            preferredAnswerScore: formatScoreScaled(+value)
          };
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: updatedRequirementConditions
    });
  };

  const handleChangeConversationStatus = (value: ConversationStatus) => {
    const updatedRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (
          condition.type ===
          RequirementConditionType.ConversationStatusCondition
        ) {
          return {
            ...condition,
            conversationStatus: value
          };
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: updatedRequirementConditions
    });
  };

  const conversationScoreField = getConversationAutomationScore(
    analysisAutomationItem
  );
  const conversationStatus = getConversationAutomationStatus(
    analysisAutomationItem
  );

  return (
    <Stack direction="column" spacing={2}>
      <Dropdown
        field={{
          containerSx: { width: rulesetInputWidth, mb: '15px' },
          label: "Change the applicant's status to",
          options: getStatusOptions({ statusSet })
        }}
        value={analysisAutomationItem.action.candidateStatus || ''}
        onChange={handleChangeCandidateStatus}
      />
      <Dropdown
        field={{
          containerSx: { width: rulesetInputWidth },
          label: 'When campaign',
          options: conversationAutomationTaskTypeOptions
        }}
        value={getConversationAutomationTaskType(analysisAutomationItem)}
        onChange={handleChangeConversationAutomationTaskType}
      />
      {getConversationAutomationTaskType(analysisAutomationItem) ===
        RuleConversationAutomationType.CONVERSATION_STATUS && (
        <Dropdown
          field={{
            options: conversationAutomationStatusOptions,
            containerSx: {
              width: rulesetInputWidth
            }
          }}
          value={conversationStatus}
          onChange={handleChangeConversationStatus}
        />
      )}
      {getConversationAutomationTaskType(analysisAutomationItem) ===
        RuleConversationAutomationType.CONVERSATION_SCORE && (
        <Stack
          direction="row"
          gap={3}
          sx={{ maxWidth: 'fit-content', alignItems: 'center' }}
        >
          <Dropdown
            field={{
              options: scoreConditionOptions,
              containerSx: {
                width: rulesetInputWidth
              }
            }}
            value={getConversationAutomationScoreOperator(
              analysisAutomationItem
            )}
            onChange={handleChangeConversationScoreOperator}
          />
          <Input
            type="number"
            labelContainer={{
              mb: 0
            }}
            sx={rulesetInputContainer}
            inputProps={{ min: 0, max: 100 }}
            name={conversationScoreField.toString()}
            id={conversationScoreField.toString()}
            onChange={(e) => handleChangeConversationScore(e.target.value)}
            value={getScoreScaleFormatter(conversationScoreField)}
          />
          <Typography>%</Typography>
        </Stack>
      )}
    </Stack>
  );
};
